import { useNavigate, useParams } from "react-router-dom";
import { useApiProvider } from "../providers/api/apiProvider";
import { MutableRefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Queue } from "../models/Queue";
import { Service, ServiceStatus } from "../models/Service";
import { abandonService, closeService, DefaultServicePayload, deleteMessage, DeleteMessagePayload, editMessage, EditMessagePayload, getServices, GetServicesPayload, getTotalWaiting, GetTotalWaiting, Order, reactMessage, ReactMessagePayload, retryMessage, RetryMessagePayload, sendMessage, SendMessagePayload, startConversation, StartConversationPayload, transferService, TransferServicePayload } from "../services/serviceServices";
import { getConversations, GetConversationsPayload, getMessageContext, getMessagesByChat, getServiceByChatId, GetMessageContextPayload, GetMessagesByChatPayload, MessageContext, markChatRead } from "../services/chatServices";
import { useStore } from "../store";
import { useSocket } from "../providers/socket/socketProvider";
import { Message, MessageStatus, MessageType, OwnerTypeMessage } from "../models/Message";
import { format } from "date-fns";
import { parseHtmlToChannelMarkup, parseChannelMarkupToHtml } from "../utils/channelAndHtml";
import { Extension, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { ChannelInputRules } from "../utils/channelInputRules";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { VariableMark } from "../utils/variableMark";
import { VariableInsertCommand } from "../utils/variableInsertCommand";
import { VariableKeyboardShortCut } from "../utils/variableKeyboardShortCut";
import { Channel, mergeChannelConnectionFallback, ChannelStatus } from "../models/Channel";
import { listChannels } from "../services/channelServices";
import { getAllQueues, GetAllQueuesPayload } from "../services/queueServices";
import { User } from "../models/User";

import {
  v4 as uuid,
} from "uuid";
import { getAudioRateLocalStorage, setAudioRateLocalStorage } from "../services/localStorageServices";
import { FileType } from "../types/FileType";
import { useMultipartUpload } from "../hooks/useMultipartUpload";
import { applyPresenceToService, applyPresenceToServices, mergeRealtimeMessages, mergeRealtimeServiceList, mergeSelectedRealtimeService, promoteServiceAfterReply, reconcileOptimisticMessage, sortRealtimeServices, updateMessagesStatus } from "./chatRealtime";
import { normalizeMessage, normalizeService } from "../services/messageAdapters";
import { Chat } from "../models/Chat";
import { PresenceItem, PresenceSnapshot } from "../models/Presence";
import { getAssignedRole } from "../services/accessControl";

export const defaultQueue = { name: 'Todas'};
export const defaultChannel = { id: "", name: "Todos" } as Channel;

const mergeServiceChannelFallback = ( service: Service, fallback?: Service ) => {
  const fallbackChannel = fallback?.chat.channel;
  const currentChannel = service.chat.channel;
  const fallbackQueue = fallback?.queue;
  const currentQueue = service.queue;
  const fallbackContact = fallback?.chat.contact;
  const currentContact = service.chat.contact;
  const nextQueue = currentQueue?.name || !fallbackQueue?.name
    ? currentQueue
    : !currentQueue || currentQueue.id === fallbackQueue.id
      ? { ...fallbackQueue, ...currentQueue, name: fallbackQueue.name }
      : currentQueue;
  const nextContact = typeof currentContact?.isOnline === "boolean" || typeof fallbackContact?.isOnline !== "boolean"
    ? currentContact
    : { ...currentContact, isOnline: fallbackContact.isOnline };

  if(( currentChannel?.name || !fallbackChannel?.name ) && nextQueue === currentQueue && nextContact === currentContact ) return service;

  return {
    ...service,
    queue: nextQueue,
    chat: {
      ...service.chat,
      channelId: currentChannel?.name ? service.chat.channelId : service.chat.channelId || fallback?.chat.channelId || fallbackChannel?.id,
      channel: currentChannel?.name || !fallbackChannel?.name ? currentChannel : fallbackChannel,
      contact: nextContact,
    },
  };
};

const perPageServices = 15;
const perPageMessages = 20;
const searchDebounceMs = 400;
const scrollToBottomTolerance = 4;
const forcedScrollFinalDelayMs = 140;
const suppressScrollToBottomButtonMs = forcedScrollFinalDelayMs + 240;
const paginationErrorRetryCooldownMs = 1500;
export const defaultServiceOrder = Order.DESC;
const draftIDPrefix = "draft-";
const draftStoragePrefix = "void-chat:draft-service:";

const isDraftService = ( service: Service | null ) => Boolean( service?.isDraft || service?.id.startsWith( draftIDPrefix ) || service?.chat?.id.startsWith( draftIDPrefix ));
const isHistoricalConversationService = ( service: Service | null ) => Boolean( service && isDraftService( service ) && !service.chat?.id.startsWith( draftIDPrefix ));
const activeServiceTabStatus = ( service: Service | null ): ServiceStatus | null => {
  if( !service || isDraftService( service )) return null;
  if([ ServiceStatus.PROGRESS, ServiceStatus.WAITING, ServiceStatus.AUTOMATED ].includes( service.status )) return service.status;
  return null;
};
export const shouldMarkServiceChatRead = ( service: Service | null ) => Boolean( service?.chat?.id && !isDraftService( service ));
export const shouldCloseServiceChatOnEscape = ( event: Pick<KeyboardEvent, "key" | "defaultPrevented">, service: Service | null, dialogOpen: boolean ) => (
  event.key === "Escape" && !event.defaultPrevented && Boolean( service ) && !dialogOpen
);
const draftStorageKey = ( chatId: string ) => `${ draftStoragePrefix }${ chatId }`;

type LocalUploadRetryPayload = {
  contentMessage: string;
  attachment: File;
  attachmentType: FileType;
  fileMetadata?: SendMessagePayload["fileMetadata"];
};

type AttachmentSelectionType = FileType | "media";
type SendMessageHandler = ( contentMessage: string, attachments?: Array<File> | null, attachmentType?: AttachmentSelectionType | null, fileMetadata?: SendMessagePayload["fileMetadata"] ) => Promise<void>;

const parseDraftDate = ( value: unknown ) => value ? new Date( value as string ) : null as unknown as Date;

const attachmentTypeFromFile = ( file: File, selectedType: AttachmentSelectionType | null ): FileType | null => {
  if( selectedType !== "media" ) return selectedType;

  const mimeGroup = file.type.split("/").shift();
  if( mimeGroup === "image" ) return "image";
  if( mimeGroup === "video" ) return "video";
  return null;
};

const getClipboardImageFileExtension = ( type: string ): string => {
  const subtype = type.split("/")[ 1 ]?.split(";")[ 0 ];
  if( !subtype ) return "png";
  if( subtype === "jpeg" ) return "jpg";
  if( subtype === "svg+xml" ) return "svg";
  return subtype;
};

const normalizeClipboardImageFile = ( file: File, index: number ): File => {
  if( file.name ) return file;

  const type = file.type || "image/png";
  const extension = getClipboardImageFileExtension( type );
  const timestamp = Date.now();
  return new File([ file ], `imagem-colada-${ timestamp }-${ index + 1 }.${ extension }`, {
    type,
    lastModified: timestamp,
  });
};

const getClipboardImageFiles = ( clipboardData: DataTransfer | null ): Array<File> => {
  if( !clipboardData ) return [];

  const filesFromItems = Array.from( clipboardData.items )
    .filter(( item ) => item.kind === "file" && item.type.startsWith( "image/" ))
    .map(( item ) => item.getAsFile())
    .filter(( file ): file is File => Boolean( file ))
    .map( normalizeClipboardImageFile );

  if( filesFromItems.length > 0 ) return filesFromItems;

  return Array.from( clipboardData.files )
    .filter(( file ) => file.type.startsWith( "image/" ))
    .map( normalizeClipboardImageFile );
};

const storeDraftService = ( service: Service ) => {
  window.sessionStorage.setItem( draftStorageKey( service.chat.id ), JSON.stringify( service ));
};

const loadDraftService = ( chatId: string ): Service | null => {
  const raw = window.sessionStorage.getItem( draftStorageKey( chatId ));
  if( !raw ) return null;

  try {
    const service = JSON.parse( raw ) as Service;
    service.createdAt = parseDraftDate( service.createdAt );
    service.updatedAt = parseDraftDate( service.updatedAt );
    service.deletedAt = parseDraftDate( service.deletedAt );
    service.chat.createdAt = parseDraftDate( service.chat.createdAt );
    service.chat.updatedAt = parseDraftDate( service.chat.updatedAt );
    service.chat.deletedAt = parseDraftDate( service.chat.deletedAt );
    service.chat.contact.createdAt = parseDraftDate( service.chat.contact.createdAt );
    service.chat.contact.updatedAt = parseDraftDate( service.chat.contact.updatedAt );
    service.chat.contact.deletedAt = parseDraftDate( service.chat.contact.deletedAt );

    return service;
  } catch {
    window.sessionStorage.removeItem( draftStorageKey( chatId ));
    return null;
  }
};

const removeDraftService = ( chatId: string ) => {
  window.sessionStorage.removeItem( draftStorageKey( chatId ));
};

const conversationToService = ( chat: Chat, user?: User | null ): Service => {
  const activeService = chat.services?.find(( service ) => service.status !== ServiceStatus.FINALIZED );
  if( activeService ) {
    return {
      ...activeService,
      isDraft: false,
      chat: {
        ...chat,
        services: undefined,
      },
      queue: activeService.queue ?? defaultQueue as Queue,
    };
  }

  const now = new Date();

  return {
    id: `${ draftIDPrefix }${ chat.id }`,
    isDraft: true,
    unReadCount: 0,
    status: ServiceStatus.PROGRESS,
    acceptedTerms: false,
    createdAt: chat.createdAt ?? now,
    updatedAt: chat.updatedAt ?? now,
    deletedAt: null as unknown as Date,
    queue: defaultQueue as Queue,
    user: user as User,
    chat: {
      ...chat,
      channelId: chat.channelId || chat.channel?.id,
      channel: chat.channel ?? {
        id: chat.channelId ?? "",
        status: ChannelStatus.DISCONNECTED,
        updatedAt: now,
      },
    },
  };
};

const messageTimestamp = ( message: Message ): number => {
  const parsed = message.createdAt ? new Date( message.createdAt ).getTime() : 0;
  return Number.isNaN( parsed ) ? 0 : parsed;
};

const sortMessagesByNewest = ( messages: Array<Message> ): Array<Message> => [ ...messages ].sort(( firstMessage, secondMessage ) => {
  const timestampDiff = messageTimestamp( secondMessage ) - messageTimestamp( firstMessage );
  if( timestampDiff !== 0 ) return timestampDiff;
  return secondMessage.id.localeCompare( firstMessage.id );
});

const mergeMessageWindow = ( currentMessages: Array<Message>, incomingMessages: Array<Message> ): Array<Message> => {
  const byID = new Map<string, Message>();

  currentMessages.forEach(( message ) => byID.set( message.id, message ));
  incomingMessages.forEach(( message ) => byID.set( message.id, {
    ...byID.get( message.id ),
    ...message,
  }));

  return sortMessagesByNewest( Array.from( byID.values() ));
};

const replaceMessage = ( messages: Array<Message> | undefined, updatedMessage: Message ): Array<Message> => (
  messages?.map(( message ) => message.id === updatedMessage.id ? updatedMessage : message ) ?? []
);

const replaceServiceMessage = ( service: Service, updatedMessage: Message ): Service => ({
  ...service,
  chat: {
    ...service.chat,
    messages: replaceMessage( service.chat.messages, updatedMessage ),
  },
});

export const useChatController = () => {
  const scrollServicesRef = useRef<HTMLDivElement | null>( null );
  const scrollMessagesRef = useRef<HTMLDivElement | null>( null );

  const attachmentInputRef = useRef<HTMLInputElement | null>( null );

  const attachmentsRef = useRef<Array<File>>( [] );
  const attachmentTypeRef = useRef<AttachmentSelectionType | null>( null );
  const handleSendMessageRef = useRef<SendMessageHandler | null>( null );
  const replyTargetMessageRef = useRef<Message | null>( null );
  const editTargetMessageRef = useRef<Message | null>( null );
  const internalMessageRef = useRef<boolean>( false );

  const {
    chatId,
  } = useParams();

  const navigate = useNavigate();

  const {
    simpleApiCall,
    paginatedApiCall,
  } = useApiProvider();

  const {
    loading,
    user,
  } = useStore();
  const assignedRole = getAssignedRole( user );

  const {
    socket,
  } = useSocket();

  const [ showMenuFilter, setShowMenuFilter ] = useState<boolean>( false );

  const [ loadingServices, setLoadingServices ] = useState<boolean>( false );

  const [ loadingMessages, setLoadingMessages ] = useState<boolean>( false );
  const [ loadingMessagesPage, setLoadingMessagesPage ] = useState<number>( 1 );

  const [ showServiceMenu, setShowServiceMenu ] = useState<boolean>( false );
  const [ showAttachmentMenu, setShowAttachmentMenu ] = useState<boolean>( false );

  const [ showTransferModalConfirm, setShowTransferModalConfirm ] = useState<boolean>( false );
  const [ showAbandonModalConfirm, setShowAbandonModalConfirm ] = useState<boolean>( false );
  const [ showCloseModalConfirm, setShowCloseModalConfirm ] = useState<boolean>( false );
  const [ showTransferServiceModal, setShowTransferServiceModal ] = useState<boolean>( false );
  const [ showStartConversationModal, setShowStartConversationModal ] = useState<boolean>( false );

  const [ showScrollToBottom, setShowScrollToBottom ] = useState<boolean>( false );

  const [ servicesPage, setServicesPage ] = useState<number>( 1 );
  const [ servicesTotal, setServicesTotal ] = useState<number>( 0 );

  const [ messagesPage, setMessagesPage ] = useState<number>( 1 );
  const [ messagesTotal, setMessagesTotal ] = useState<number>( 0 );

  const [ totalWaiting, setTotalWaiting ] = useState<number>( 0 );
  const [ search, setSearch ] = useState<string>('');
  const [ debouncedSearch, setDebouncedSearch ] = useState<string>('');
  const [ queue, setQueue ] = useState<Queue | null>( defaultQueue as Queue );
  const [ channel, setChannel ] = useState<Channel | null>( defaultChannel );
  const [ status, setStatus ] = useState<ServiceStatus>( ServiceStatus.PROGRESS );
  const [ order, setOrder ] = useState<Order>( defaultServiceOrder );

  const [ queues, setQueues ] = useState<Array<Queue>>([ defaultQueue ] as Array<Queue> );
  const [ channels, setChannels ] = useState<Array<Channel>>([ defaultChannel ]);
  const [ services, setServices ] = useState<Array<Service>>([]);

  const [ messages, setMessages ] = useState<Array<Message>>([]);

  const [ selectedServiceChat, setSelectedServiceChat ] = useState<Service | null>( null );

  const [ message, setMessage ] = useState<string>("");
  const [ internalMessage, setInternalMessage ] = useState<boolean>( false );
  const [ replyTargetMessage, setReplyTargetMessage ] = useState<Message | null>( null );
  const [ editTargetMessage, setEditTargetMessage ] = useState<Message | null>( null );

  const [ attachmentType, setAttachmentType ] = useState<AttachmentSelectionType | null>( null );
  const [ attachments, setAttachments ] = useState<Array<File>>( [] );
  const servicesRequestRef = useRef<number>( 0 );
  const totalWaitingRequestRef = useRef<number>( 0 );
  const messagesRequestRef = useRef<number>( 0 );
  const servicesPaginationInFlightRef = useRef<Set<string>>( new Set());
  const messagesPaginationInFlightRef = useRef<Set<string>>( new Set());
  const servicesPaginationRetryAtRef = useRef<Map<string, number>>( new Map());
  const messagesPaginationRetryAtRef = useRef<Map<string, number>>( new Map());
  const serviceMutationInFlightRef = useRef<boolean>( false );
  const sendMessageInFlightRef = useRef<boolean>( false );
  const retryMessageInFlightRef = useRef<Set<string>>( new Set());
  const [ retryingMessageIds, setRetryingMessageIds ] = useState<Set<string>>( new Set());
  const forceScrollToLatestMessageRef = useRef<boolean>( false );
  const forceScrollToMessageIDRef = useRef<string | null>( null );
  const suppressScrollToBottomButtonUntilRef = useRef<number>( 0 );
  const selectedChatIdRef = useRef<string | undefined>( undefined );
  const selectedServiceChatRef = useRef<Service | null>( null );
  const servicesRef = useRef<Array<Service>>([]);
  const draftServiceRef = useRef<Service | null>( null );
  const uploadRetryPayloadsRef = useRef<Record<string, LocalUploadRetryPayload>>( {} );
  const currentUploadMessageIDRef = useRef<string | null>( null );
  const {
    state: multipartUploadState,
    multipartEnabled,
    ensureCompletedUpload,
    abortUpload,
    resetUpload,
  } = useMultipartUpload({
    scope: "chat-message",
  });

  const beginPaginationRequest = (
    inFlightRef: MutableRefObject<Set<string>>,
    retryAtRef: MutableRefObject<Map<string, number>>,
    key: string,
    setLoading: ( value: boolean ) => void,
  ): boolean => {
    if( inFlightRef.current.has( key )) return false;

    const retryAt = retryAtRef.current.get( key ) ?? 0;
    if( retryAt > Date.now()) return false;
    retryAtRef.current.delete( key );

    inFlightRef.current.add( key );
    setLoading( true );
    return true;
  };

  const finishPaginationRequest = (
    inFlightRef: MutableRefObject<Set<string>>,
    key: string,
    setLoading: ( value: boolean ) => void,
  ) => {
    inFlightRef.current.delete( key );
    setLoading( inFlightRef.current.size > 0 );
  };

  const markPaginationRequestFailed = (
    retryAtRef: MutableRefObject<Map<string, number>>,
    key: string,
  ) => {
    retryAtRef.current.set( key, Date.now() + paginationErrorRetryCooldownMs );
  };

  const clearPaginationRequestFailure = (
    retryAtRef: MutableRefObject<Map<string, number>>,
    key: string,
  ) => {
    retryAtRef.current.delete( key );
  };

  const servicesPaginationKey = ( page: number ) => [
    "services",
    page,
    status,
    queue?.id ?? "",
    channel?.id ?? "",
    debouncedSearch,
    order,
  ].join( "|" );

  const messagesPaginationKey = ( chatId: string, page: number ) => [ "messages", chatId, page ].join( "|" );

  const [ currentAudio, setCurrentAudio ] = useState<string | null>( null );
  const [ audioRate, setAudioRate ] = useState<number>( 1 );

  const handlePlayAudio = ( id: string ) => setCurrentAudio( id );
  const handleStopAudio = () => setCurrentAudio( null );

  const handleNextAudioRate = () => {
    const audioRateMapper: {
      [ key: number ]: number;
    } = {
      1: 1.5,
      1.5: 2,
      2: 1,
    };

    setAudioRate(( value ) => {
      const newValue = audioRateMapper[ value ];

      setAudioRateLocalStorage( newValue );

      return newValue;
    });
  };

  const handleToggleShowServiceMenu = ( showServiceMenu: boolean ) => setShowServiceMenu( showServiceMenu );
  const handleToggleShowAttachmentMenu = ( showAttachmentMenu: boolean ) => setShowAttachmentMenu( showAttachmentMenu );

  const selectedServiceChannelStatus = useMemo(() => {
    const defaultChannel = user?.organization.channel;
    const connections = mergeChannelConnectionFallback(user?.organization.channelConnections ?? [], defaultChannel);
    const selectedChannel = selectedServiceChat?.chat?.channel;
    const selectedChannelID = selectedServiceChat?.chat?.channelId || selectedChannel?.id;

    if( selectedChannelID ) {
      return connections.find((connection) => connection.id === selectedChannelID)?.status
        ?? selectedChannel?.status
        ?? ChannelStatus.DISCONNECTED;
    }

    return defaultChannel?.status ?? ChannelStatus.DISCONNECTED;
  }, [selectedServiceChat?.chat?.channel, selectedServiceChat?.chat?.channelId, user?.organization.channel, user?.organization.channelConnections]);
  const selectedServiceChannelIsConnected = selectedServiceChannelStatus === ChannelStatus.CONNECTED;

  const handleToggleShowTransferModalConfirm = ( showTransferModalConfirm: boolean ) => setShowTransferModalConfirm( showTransferModalConfirm );
  const handleToggleShowAbandonModalConfirm = ( showAbandonModalConfirm: boolean ) => setShowAbandonModalConfirm( showAbandonModalConfirm );
  const handleToggleShowCloseModalConfirm = ( showCloseModalConfirm: boolean ) => setShowCloseModalConfirm( showCloseModalConfirm );

  const handleToggleShowTransferServiceModal = ( showTransferServiceModal: boolean ) => {
    setShowTransferServiceModal( showTransferServiceModal );

    if( showTransferServiceModal ) setShowTransferModalConfirm( false );
  };

  const handleToggleShowStartConversationModal = ( showStartConversationModal: boolean ) => setShowStartConversationModal( showStartConversationModal );

  const handleToggleInternalMessage = () => {
    setInternalMessage(( value ) => {
      internalMessageRef.current = !value;
      return !value;
    });
    setAttachments( [] );
    setAttachmentType( null );
    replyTargetMessageRef.current = null;
    setReplyTargetMessage( null );
    handleToggleShowAttachmentMenu( false );
    void abortUpload();
    updateAttachmentPlaceholder( false );
  };

  const updateAttachmentPlaceholder = ( hasAttachments: boolean ) => {
    if ( editor !== null ) {
      editor.extensionManager.extensions.filter(( extension ) => extension.name === "placeholder")[ 0 ].options["placeholder"] = hasAttachments ? "Digite uma legenda" : "Digite uma mensagem";

      editor.view.dispatch( editor.state.tr );
    }
  };

  const handlePasteImageAttachment = ( event: ClipboardEvent ): boolean => {
    if( internalMessageRef.current || editTargetMessageRef.current ) return false;

    const pastedImages = getClipboardImageFiles( event.clipboardData );
    if( pastedImages.length === 0 ) return false;

    event.preventDefault();
    event.stopPropagation();

    if( multipartEnabled && attachmentsRef.current.length > 0 ) {
      void abortUpload();
    }

    setAttachmentType( "image" );
    attachmentTypeRef.current = "image";
    setAttachments( pastedImages );
    attachmentsRef.current = pastedImages;
    updateAttachmentPlaceholder( true );

    return true;
  };

  const handleChangeAttachment = ( files: FileList | null ) => {
    const nextAttachments = files ? Array.from( files ) : [];

    if( nextAttachments.length > 0 ) {
      if( multipartEnabled && attachments.length > 0 ) {
        void abortUpload();
      }

      setAttachments( nextAttachments );
    } else setAttachments( [] );

    if( nextAttachments.length === 0 ) {
      void abortUpload();
    }

    updateAttachmentPlaceholder( nextAttachments.length > 0 );
  };

  const handleRemoveAttachment = ( index: number ) => {
    if( multipartEnabled ) void abortUpload();

    const nextAttachments = attachments.filter(( _, attachmentIndex ) => attachmentIndex !== index );
    setAttachments( nextAttachments );
    if( nextAttachments.length === 0 ) setAttachmentType( null );
    updateAttachmentPlaceholder( nextAttachments.length > 0 );

  };

  const changeAttachmentType = ( attachmentType: AttachmentSelectionType | null ) => {
    setAttachmentType( attachmentType );

    if( attachmentType ) {
      setShowAttachmentMenu( false );

      if( attachmentInputRef.current ) {
        const fileAccpetMapper: {
          [ key in AttachmentSelectionType ]: string;
        } = {
          document: "*",
          audio: "audio/*",
          image: "image/*",
          video: "video/*",
          media: "image/*,video/*",
          sticker: "image/webp,image/*",
        };

        attachmentInputRef.current.accept = fileAccpetMapper[ attachmentType ];
        attachmentInputRef.current.multiple = attachmentType !== "sticker";
        attachmentInputRef.current.click();
      }
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        code: {
          HTMLAttributes: {
            style: "background:#38BDF821; padding:2px 4px; border-radius:4px; font-family:monospace; white-space:pre-wrap;",
          }
        },
      }),
      ChannelInputRules,
      Link.configure({
        autolink: true,
        openOnClick: false,
      }),
      Placeholder.configure({
        placeholder: "Digite uma mensagem",
      }),
      VariableMark,
      VariableInsertCommand,
      VariableKeyboardShortCut,
      Extension.create({
        name: "confirmOnEnter",

        addKeyboardShortcuts() {
          return {
            Enter: ({ editor }) => {
              const { from } = editor.state.selection;

              const nodeBefore = editor.state.doc.nodeAt( from - 1 );
              const nodeAfter = editor.state.doc.nodeAt( from );

              const isInsideVariable =
                nodeBefore?.marks?.some(mark => mark.type.name === "variable") ||
                nodeAfter?.marks?.some(mark => mark.type.name === "variable");

              if ( isInsideVariable ) {
                const resolvedPos = editor.state.doc.resolve( from );
                const nextPos = resolvedPos.nodeAfter ? from + resolvedPos.nodeAfter.nodeSize : from;

                editor.chain().focus()
                  .setTextSelection( nextPos )
                  .unsetMark("variable")
                  .run();

                return true;
              }

              handleSendMessageRef.current?.(
                parseHtmlToChannelMarkup( editor.getHTML()),
                attachmentsRef.current,
                attachmentTypeRef.current,
              );

              editor.chain().focus().clearContent().run();
              setMessage("");
              setAttachments( [] );
              setAttachmentType( null );

              return true;
            },
          }
        },
      })
    ],
    editorProps: {
      attributes: {
        role: "textbox",
        "aria-label": "Mensagem",
        "aria-multiline": "true",
      },
      handlePaste: ( _view, event ) => handlePasteImageAttachment( event ),
    },
    content: parseChannelMarkupToHtml( message ),
    autofocus: "end",
    editable: selectedServiceChannelIsConnected,
    onUpdate: ({ editor }) => setMessage( parseHtmlToChannelMarkup( editor.getHTML())),
  }, [ selectedServiceChat?.id, selectedServiceChannelIsConnected ]);

  const handleToggleOrder = () => {
    const newValueOrderMapper: {
      [ key in Order ]: Order;
    } = {
      [ Order.ASC ]: Order.DESC,
      [ Order.DESC ]: Order.ASC,
    };

    setOrder(( order ) => newValueOrderMapper[ order ]);
  };

  const clearEditTargetMessage = useCallback(() => {
    editTargetMessageRef.current = null;
    setEditTargetMessage( null );
    editor?.chain().focus().clearContent().run();
    setMessage( "" );
  }, [ editor ]);

  const scrollMessagesToBottomNow = useCallback(( messageID?: string | null ) => {
    suppressScrollToBottomButtonUntilRef.current = Date.now() + suppressScrollToBottomButtonMs;

    const scroll = ( behavior: ScrollBehavior = "smooth", exact = false ) => {
      const scrollElement = scrollMessagesRef.current;

      if( !scrollElement ) return;

      scrollElement.scrollTo({
        top: 0,
        behavior,
      });

      if( messageID ) {
        const targetMessage = Array.from( scrollElement.querySelectorAll<HTMLElement>( "[data-message-id]" ))
          .find(( element ) => element.getAttribute( "data-message-id" ) === messageID );

        targetMessage?.scrollIntoView({
          block: "end",
          inline: "nearest",
          behavior,
        });

        if( exact ) scrollElement.scrollTop = 0;
      }

      setShowScrollToBottom( false );
    };

    scroll();
    window.requestAnimationFrame(() => {
      scroll();
      window.requestAnimationFrame(() => scroll());
    });
    window.setTimeout(() => scroll( "auto", true ), forcedScrollFinalDelayMs );
  }, []);

  const forceScrollMessagesToBottom = useCallback(( messageID?: string | null ) => {
    forceScrollToLatestMessageRef.current = true;
    forceScrollToMessageIDRef.current = messageID ?? forceScrollToMessageIDRef.current;
    scrollMessagesToBottomNow( forceScrollToMessageIDRef.current );
  }, [ scrollMessagesToBottomNow ]);

  const updateLocalUploadMessage = useCallback(( messageID: string, next: Partial<NonNullable<Message["localUpload"]>> ) => {
    setMessages(( messages ) => messages.map(( message ) => message.id === messageID
      ? {
          ...message,
          localUpload: {
            status: "UPLOADING",
            percentage: 0,
            ...message.localUpload,
            ...next,
          },
        }
      : message
    ));
  }, []);

  const markLocalUploadFailed = useCallback(( messageID: string, error?: unknown ) => {
    const message = error instanceof Error ? error.message : "Falha ao enviar arquivo";
    if( currentUploadMessageIDRef.current === messageID ) currentUploadMessageIDRef.current = null;
    updateLocalUploadMessage( messageID, {
      status: "FAILED",
      error: message || "Falha ao enviar arquivo",
      retryable: Boolean( uploadRetryPayloadsRef.current[ messageID ] ),
    });
  }, [ updateLocalUploadMessage ]);

  useEffect(() => {
    const messageID = currentUploadMessageIDRef.current;
    if( !messageID || !multipartEnabled ) return;

    if( multipartUploadState.status === "FAILED" ) {
      markLocalUploadFailed( messageID, multipartUploadState.error );
      return;
    }

    if([ "INITIATING", "UPLOADING", "COMPLETING", "COMPLETED" ].includes( multipartUploadState.status )) {
      updateLocalUploadMessage( messageID, {
        status: "UPLOADING",
        percentage: multipartUploadState.status === "COMPLETING" || multipartUploadState.status === "COMPLETED" ? 100 : multipartUploadState.percentage,
        retryable: false,
      });
    }
  }, [ markLocalUploadFailed, multipartEnabled, multipartUploadState.error, multipartUploadState.percentage, multipartUploadState.status, updateLocalUploadMessage ]);

  const handleSendMessage = useCallback( async ( contentMessage: string, attachments?: Array<File> | null, attachmentType?: AttachmentSelectionType | null, fileMetadata?: SendMessagePayload["fileMetadata"] ) => {
    if( sendMessageInFlightRef.current ) return;

    const attachmentFiles = attachments ?? [];
    const selectedAttachmentType = attachmentType ?? null;
    const isWithAttachment = Boolean( selectedAttachmentType ) && ( attachmentFiles.length > 0 || Boolean( fileMetadata ));
    const currentReplyTargetMessage = replyTargetMessageRef.current;
    const currentEditTargetMessage = editTargetMessageRef.current;

    if( currentEditTargetMessage ) {
      const trimmedContent = contentMessage.trim();
      if( !selectedServiceChat || isWithAttachment || !trimmedContent ) return;

      sendMessageInFlightRef.current = true;

      try {
        const response = await simpleApiCall<EditMessagePayload, Message>( editMessage, {
          id: selectedServiceChat.id,
          messageId: currentEditTargetMessage.id,
          message: contentMessage,
        }, () => {});

        if( response ) {
          setMessages(( messages ) => replaceMessage( messages, response ));
          setSelectedServiceChat(( service ) => service ? replaceServiceMessage( service, response ) : service );
          setServices(( services ) => services.map(( service ) => service.id === selectedServiceChat.id ? replaceServiceMessage( service, response ) : service ));

          editor?.chain().focus().clearContent().run();
          setMessage( "" );
          clearEditTargetMessage();
        }
      } finally {
        sendMessageInFlightRef.current = false;
      }

      return;
    }

    if( internalMessage ) {
      const trimmedContent = contentMessage.trim();
      if( !selectedServiceChat || selectedServiceChat.isDraft || !trimmedContent ) return;

      sendMessageInFlightRef.current = true;

      try {
        const currentDate = new Date();
        const optimisticMessageID = uuid();
        const optimisticMessage = {
          id: optimisticMessageID,
          content: trimmedContent,
          createdAt: currentDate,
          updatedAt: currentDate,
          ownerType: OwnerTypeMessage.USER,
          status: MessageStatus.SENDING,
          user: user as User,
          messageType: MessageType.INTERNAL,
        } as Message;

        setMessages(( messages ) => [ optimisticMessage, ...messages ]);
        forceScrollMessagesToBottom( optimisticMessageID );

        editor?.chain().focus().clearContent().run();
        setMessage( "" );
        setInternalMessage( false );
        replyTargetMessageRef.current = null;
        setReplyTargetMessage( null );

        const response = await simpleApiCall<SendMessagePayload, Message>( sendMessage, {
          id: selectedServiceChat.id,
          message: trimmedContent,
          internal: true,
        }, () => {});

        if( response ) {
          const promotedSelectedService = promoteServiceAfterReply( selectedServiceChat, response, user );
          const nextStatus = activeServiceTabStatus( promotedSelectedService ) ?? status;

          if( nextStatus !== status ) {
            setStatus( nextStatus );
            setServicesPage( 1 );
          }

          setMessages(( messages ) => reconcileOptimisticMessage( messages, optimisticMessageID, response ));
          setSelectedServiceChat( promotedSelectedService );
          setServices(( services ) => mergeRealtimeServiceList(
            services,
            promotedSelectedService,
            nextStatus,
            queue?.id,
            debouncedSearch,
            assignedRole,
            channel?.id,
          ));
          forceScrollMessagesToBottom( response.id );
        }
      } finally {
        sendMessageInFlightRef.current = false;
      }

      return;
    }

    if( attachmentFiles.length > 1 && selectedAttachmentType && !fileMetadata ) {
      if( !selectedServiceChat ) return;

      sendMessageInFlightRef.current = true;
      try {
        let serviceForBatch = selectedServiceChat;
        if( isDraftService( selectedServiceChat )) {
          const contact = selectedServiceChat.chat.contact;
          const serviceResponse = await simpleApiCall<StartConversationPayload, Service>( startConversation, {
            channelId: selectedServiceChat.chat.channelId || selectedServiceChat.chat.channel?.id || selectedServiceChat.chat.channel?.id,
            name: contact.name,
            phone: contact.phone,
          });

          if( serviceResponse === null ) return;
          serviceForBatch = normalizeService( serviceResponse, assignedRole );
          removeDraftService( selectedServiceChat.chat.id );
        }

        editor?.chain().focus().clearContent().run();
        setMessage( "" );
        setAttachments( [] );
        setAttachmentType( null );
        replyTargetMessageRef.current = null;
        setReplyTargetMessage( null );
        resetUpload();

        let promotedService = serviceForBatch;
        let firstResponseID = "";

        for( const [ index, file ] of attachmentFiles.entries()) {
          const resolvedAttachmentType = attachmentTypeFromFile( file, selectedAttachmentType );
          if( !resolvedAttachmentType ) continue;

          let uploadedFileMetadata = null;
          if( multipartEnabled ) {
            uploadedFileMetadata = await ensureCompletedUpload( file, {
              context: "message",
              ownerId: user?.id,
              metadata: {
                serviceId: promotedService.id,
                chatId: promotedService.chat.id,
              },
            });
            if( !uploadedFileMetadata ) continue;
          }

          const response = await simpleApiCall<SendMessagePayload, Message>( sendMessage, {
            id: promotedService.id,
            message: index === 0 ? contentMessage : "",
            replyToMessageId: index === 0 ? currentReplyTargetMessage?.id : undefined,
            attachmentType: resolvedAttachmentType,
            fileMetadata: multipartEnabled ? uploadedFileMetadata : undefined,
            attachment: multipartEnabled ? undefined : file,
          }, () => {});

          if( !response ) continue;
          if( !firstResponseID ) firstResponseID = response.id;

          promotedService = promoteServiceAfterReply( promotedService, response, user );
          const nextStatus = activeServiceTabStatus( promotedService ) ?? status;

          if( nextStatus !== status ) {
            setStatus( nextStatus );
            setServicesPage( 1 );
          }

          const nextServices = mergeRealtimeServiceList(
            servicesRef.current,
            promotedService,
            nextStatus,
            queue?.id,
            debouncedSearch,
            assignedRole,
            channel?.id,
          );
          servicesRef.current = nextServices;
          setMessages(( messages ) => [ response, ...messages ]);
          setSelectedServiceChat( promotedService );
          setServices( nextServices );
        }

        if( isDraftService( selectedServiceChat )) {
          navigate(`/chat/${ promotedService.chat.id }`);
        }
        if( firstResponseID ) forceScrollMessagesToBottom( firstResponseID );
      } finally {
        sendMessageInFlightRef.current = false;
      }

      return;
    }

    const attachment = attachmentFiles[ 0 ] ?? null;
    const resolvedAttachmentType: FileType | null = attachment
      ? attachmentTypeFromFile( attachment, selectedAttachmentType )
      : selectedAttachmentType === "media" ? null : selectedAttachmentType;

    if( selectedServiceChat && isDraftService( selectedServiceChat )) {
      if( !Boolean( contentMessage ) && !isWithAttachment ) return;

      sendMessageInFlightRef.current = true;

      try {
        const contact = selectedServiceChat.chat.contact;
        const serviceResponse = await simpleApiCall<StartConversationPayload, Service>( startConversation, {
          channelId: selectedServiceChat.chat.channelId || selectedServiceChat.chat.channel?.id || selectedServiceChat.chat.channel?.id,
          name: contact.name,
          phone: contact.phone,
        });

        if( serviceResponse === null ) return;

        const persistedService = normalizeService( serviceResponse, assignedRole );
        let uploadedFileMetadata = null;
        const optimisticMessageID = uuid();

        if( isWithAttachment && attachment && resolvedAttachmentType ) {
          const currentDate = new Date();
          uploadRetryPayloadsRef.current[ optimisticMessageID ] = {
            contentMessage,
            attachment,
            attachmentType: resolvedAttachmentType,
            fileMetadata,
          };
          currentUploadMessageIDRef.current = optimisticMessageID;
          setMessages([ {
            id: optimisticMessageID,
            content: contentMessage,
            createdAt: currentDate,
            updatedAt: currentDate,
            ownerType: OwnerTypeMessage.USER,
            status: MessageStatus.SENDING,
            user: user as User,
            messageType: MessageType.LOADING,
            replyToMessageId: currentReplyTargetMessage?.id,
            localUpload: {
              status: "UPLOADING",
              percentage: fileMetadata ? 100 : 0,
              fileName: attachment.name,
              retryable: false,
            },
          } as Message ]);
          forceScrollMessagesToBottom( optimisticMessageID );
        }

        if( isWithAttachment && multipartEnabled && attachment ) {
          try {
            uploadedFileMetadata = await ensureCompletedUpload( attachment, {
              context: "message",
              ownerId: user?.id,
              metadata: {
                serviceId: persistedService.id,
                chatId: persistedService.chat.id,
              },
            });
          } catch ( error ) {
            markLocalUploadFailed( optimisticMessageID, error );
            return;
          }

          if( !uploadedFileMetadata ) {
            markLocalUploadFailed( optimisticMessageID );
            return;
          }
        }

        const payload: SendMessagePayload = {
          id: persistedService.id,
          message: contentMessage,
          replyToMessageId: currentReplyTargetMessage?.id,
        };

        if( isWithAttachment ) {
          payload.attachmentType = resolvedAttachmentType;

          if( fileMetadata ) payload.fileMetadata = fileMetadata;
          else if( multipartEnabled && uploadedFileMetadata ) payload.fileMetadata = uploadedFileMetadata;
          else payload.attachment = attachment;

          payload.onUploadProgress = ( percentage ) => updateLocalUploadMessage( optimisticMessageID, {
            status: "UPLOADING",
            percentage,
            retryable: false,
          });
        }

        editor?.chain().focus().clearContent().run();
        setMessage( "" );
        setAttachments( [] );
        setAttachmentType( null );
        replyTargetMessageRef.current = null;
        setReplyTargetMessage( null );
        resetUpload();

        const messageResponse = await simpleApiCall<SendMessagePayload, Message>( sendMessage, payload, () => {});

        if( messageResponse ) {
          delete uploadRetryPayloadsRef.current[ optimisticMessageID ];
          currentUploadMessageIDRef.current = null;
          removeDraftService( selectedServiceChat.chat.id );
          const promotedService = promoteServiceAfterReply( persistedService, messageResponse, user );
          const nextStatus = activeServiceTabStatus( promotedService ) ?? status;

          if( nextStatus !== status ) {
            setStatus( nextStatus );
            setServicesPage( 1 );
          }

          setServices(( services ) => mergeRealtimeServiceList(
            services,
            promotedService,
            nextStatus,
            queue?.id,
            debouncedSearch,
            assignedRole,
            channel?.id,
          ));
          setSelectedServiceChat( promotedService );
          setMessages([ messageResponse ]);
          navigate(`/chat/${ promotedService.chat.id }`);
          forceScrollMessagesToBottom( messageResponse.id );
        } else if( isWithAttachment ) {
          markLocalUploadFailed( optimisticMessageID );
        }
      } finally {
        sendMessageInFlightRef.current = false;
      }

      return;
    }

    if( isWithAttachment || ( Boolean( contentMessage ) && selectedServiceChat !== null )) {
      sendMessageInFlightRef.current = true;
      let uploadedFileMetadata = null;
      let optimisticMessageID = "";

      try {
        const currentDate = new Date();

        optimisticMessageID = uuid();
        const message: Partial<Message> = {
          content: contentMessage,
          createdAt: currentDate,
          ownerType: OwnerTypeMessage.USER,
          status: MessageStatus.SENDING,
          updatedAt: currentDate,
          user: user as User,
          id: optimisticMessageID,
          messageType: MessageType.TEXT,
          replyToMessageId: currentReplyTargetMessage?.id,
        };

        const payload: SendMessagePayload = {
          id: selectedServiceChat?.id as string,
          message: contentMessage,
          replyToMessageId: currentReplyTargetMessage?.id,
        };

        if( isWithAttachment ) {
          message.messageType = MessageType.LOADING;
          message.localUpload = {
            status: "UPLOADING",
            percentage: fileMetadata ? 100 : 0,
            fileName: attachment?.name,
            retryable: false,
          };

          payload.attachmentType = resolvedAttachmentType;

          payload.onUploadProgress = ( percentage ) => updateLocalUploadMessage( optimisticMessageID as string, {
            status: "UPLOADING",
            percentage,
            retryable: false,
          });

          if( attachment && resolvedAttachmentType ) {
            uploadRetryPayloadsRef.current[ optimisticMessageID ] = {
              contentMessage,
              attachment,
              attachmentType: resolvedAttachmentType,
              fileMetadata,
            };
            currentUploadMessageIDRef.current = optimisticMessageID;
          }

          if( fileMetadata ) {
            payload.fileMetadata = fileMetadata;
          } else if( !multipartEnabled ) {
            payload.attachment = attachment;
          }
        }

        setMessages(( messages ) => [ message as Message, ...messages ]);
        forceScrollMessagesToBottom( optimisticMessageID );

        editor?.chain().focus().clearContent().run();
        setMessage("");
        setAttachments( [] );
        setAttachmentType( null );
        replyTargetMessageRef.current = null;
        setReplyTargetMessage( null );
        resetUpload();

        if( isWithAttachment && multipartEnabled && attachment && !fileMetadata ) {
          uploadedFileMetadata = await ensureCompletedUpload( attachment, {
            context: "message",
            ownerId: user?.id,
            metadata: {
              serviceId: selectedServiceChat?.id,
              chatId: selectedServiceChat?.chat.id,
            },
          });

          if( !uploadedFileMetadata ) {
            markLocalUploadFailed( optimisticMessageID );
            return;
          }

          payload.fileMetadata = uploadedFileMetadata;
        }

        const response = await simpleApiCall<SendMessagePayload, Message>( sendMessage, payload, () => {});

        if( response ) {
          delete uploadRetryPayloadsRef.current[ optimisticMessageID ];
          currentUploadMessageIDRef.current = null;
          forceScrollMessagesToBottom( response.id );

          const promotedSelectedService = selectedServiceChat
            ? promoteServiceAfterReply( selectedServiceChat, response, user )
            : null;

          setMessages(( messages ) => reconcileOptimisticMessage( messages, optimisticMessageID, response ));
          forceScrollMessagesToBottom( response.id );

          if( promotedSelectedService ) {
            const nextStatus = activeServiceTabStatus( promotedSelectedService ) ?? status;

            if( nextStatus !== status ) {
              setStatus( nextStatus );
              setServicesPage( 1 );
            }

            setSelectedServiceChat( promotedSelectedService );
            setServices(( services ) => mergeRealtimeServiceList(
              services,
              promotedSelectedService,
              nextStatus,
              queue?.id,
              debouncedSearch,
              assignedRole,
              channel?.id,
            ));
          }
        } else if( isWithAttachment ) {
          markLocalUploadFailed( optimisticMessageID );
        }

        if( selectedServiceChat?.status === ServiceStatus.WAITING ) setTotalWaiting(( totalWaiting ) => totalWaiting - 1 );
      } catch ( error ) {
        if( optimisticMessageID ) markLocalUploadFailed( optimisticMessageID, error );
      } finally {
        sendMessageInFlightRef.current = false;
      }
    }
  }, [ selectedServiceChat, internalMessage, editor, multipartEnabled, ensureCompletedUpload, user, assignedRole, resetUpload, status, queue?.id, channel?.id, debouncedSearch, clearEditTargetMessage, simpleApiCall, navigate, forceScrollMessagesToBottom, markLocalUploadFailed, updateLocalUploadMessage ]);

  useLayoutEffect(() => {
    handleSendMessageRef.current = handleSendMessage;
  }, [ handleSendMessage ]);

  const handleRetryUploadMessage = useCallback(( messageID: string ) => {
    const retryPayload = uploadRetryPayloadsRef.current[ messageID ];
    if( !retryPayload || sendMessageInFlightRef.current ) return;

    delete uploadRetryPayloadsRef.current[ messageID ];
    if( currentUploadMessageIDRef.current === messageID ) currentUploadMessageIDRef.current = null;
    setMessages(( messages ) => messages.filter(( message ) => message.id !== messageID ));
    void handleSendMessage(
      retryPayload.contentMessage,
      [ retryPayload.attachment ],
      retryPayload.attachmentType,
      retryPayload.fileMetadata,
    );
  }, [ handleSendMessage ]);

  const handleRetryMessage = useCallback(async ( message: Message ) => {
    const serviceID = selectedServiceChatRef.current?.id;
    if( !serviceID || retryMessageInFlightRef.current.has( message.id )) return false;

    retryMessageInFlightRef.current.add( message.id );
    setRetryingMessageIds( new Set( retryMessageInFlightRef.current ));

    try {
      const response = await simpleApiCall<RetryMessagePayload, Message>( retryMessage, {
        serviceId: serviceID,
        messageId: message.id,
      }, () => {});

      if( !response ) return false;

      if( selectedServiceChatRef.current?.id === serviceID ) {
        setMessages(( messages ) => replaceMessage( messages, response ));
      }
      setSelectedServiceChat(( service ) => service?.id === serviceID ? replaceServiceMessage( service, response ) : service );
      setServices(( services ) => services.map(( service ) => service.id === serviceID ? replaceServiceMessage( service, response ) : service ));
      return true;
    } finally {
      retryMessageInFlightRef.current.delete( message.id );
      setRetryingMessageIds( new Set( retryMessageInFlightRef.current ));
    }
  }, [ simpleApiCall ]);

  const handleSendSavedSticker = useCallback(( fileMetadata: SendMessagePayload["fileMetadata"] ) => {
    if( !fileMetadata ) return;
    handleSendMessage( "", null, "sticker", fileMetadata );
  }, [ handleSendMessage ]);

  const handleReplyToMessage = useCallback(( message: Message ) => {
    clearEditTargetMessage();
    replyTargetMessageRef.current = message;
    setReplyTargetMessage( message );
    editor?.chain().focus().run();
  }, [ editor, clearEditTargetMessage ]);

  const handleClearReplyToMessage = useCallback(() => {
    replyTargetMessageRef.current = null;
    setReplyTargetMessage( null );
  }, []);

  const handleEditMessage = useCallback(( message: Message ) => {
    if( message.ownerType !== OwnerTypeMessage.USER || message.messageType !== MessageType.TEXT || message.channelDeletedAt ) return;

    editTargetMessageRef.current = message;
    setEditTargetMessage( message );
    replyTargetMessageRef.current = null;
    setReplyTargetMessage( null );
    attachmentsRef.current = [];
    attachmentTypeRef.current = null;
    setAttachments( [] );
    setAttachmentType( null );
    resetUpload();
    setMessage( message.content ?? "" );
    editor?.chain().focus().setContent( parseChannelMarkupToHtml( message.content ?? "" )).run();
  }, [ editor, resetUpload ]);

  const handleDeleteMessage = useCallback(async ( message: Message ) => {
    if( !selectedServiceChat ) return;

    const response = await simpleApiCall<DeleteMessagePayload, Message>( deleteMessage, {
      id: selectedServiceChat.id,
      messageId: message.id,
    }, () => {});

    if( response ) {
      setMessages(( messages ) => replaceMessage( messages, response ));
      setSelectedServiceChat(( service ) => service ? replaceServiceMessage( service, response ) : service );
      setServices(( services ) => services.map(( service ) => service.id === selectedServiceChat.id ? replaceServiceMessage( service, response ) : service ));

      if( editTargetMessageRef.current?.id === message.id ) {
        editor?.chain().focus().clearContent().run();
        setMessage( "" );
        clearEditTargetMessage();
      }
    }
  }, [ selectedServiceChat, editor, clearEditTargetMessage, simpleApiCall ]);

  const handleReactToMessage = useCallback(async ( message: Message, emoji: string ) => {
    if( !selectedServiceChat || !emoji.trim()) return;

    const response = await simpleApiCall<ReactMessagePayload, Message>( reactMessage, {
      id: selectedServiceChat.id,
      messageId: message.id,
      emoji: emoji.trim(),
    }, () => {});

    if( response ) {
      setMessages(( messages ) => messages.map(( item ) => item.id === response.id ? response : item ));
    }
  }, [ selectedServiceChat, simpleApiCall ]);

  const handleSelectServiceChat = ( service: Service ) => {
    if( selectedServiceChat?.chat.id === service.chat.id ) return;

    if( isDraftService( service )) storeDraftService( service );

    const nextStatus = activeServiceTabStatus( service );
    if( status === ServiceStatus.CONVERSATIONS && nextStatus ) {
      setStatus( nextStatus );
    }

    selectedChatIdRef.current = undefined;
    messagesRequestRef.current++;
    setMessages([]);
    setSelectedServiceChat( null );

    const nextChatUrl = `/chat/${ service.chat.id }`;
    window.setTimeout(() => navigate( nextChatUrl ), 0 );
  };

  const handleStartConversationFromSharedContact = useCallback(( contact: { name: string; phone: string }) => {
    if( !contact.phone ) return;

    const now = new Date();
    const draftChatID = `${ draftIDPrefix }${ uuid() }`;
    const draftContactID = `${ draftIDPrefix }${ uuid() }`;
    const draftService: Service = {
      id: `${ draftIDPrefix }${ uuid() }`,
      isDraft: true,
      unReadCount: 0,
      status: ServiceStatus.PROGRESS,
      acceptedTerms: false,
      createdAt: now,
      updatedAt: now,
      deletedAt: null as unknown as Date,
      queue: defaultQueue as Queue,
      user: user as User,
      chat: {
        id: draftChatID,
        name: contact.name,
        phone: contact.phone,
        channelId: channel?.id || undefined,
        channel: channel?.id ? channel : {
          id: "",
          status: ChannelStatus.DISCONNECTED,
          updatedAt: now,
        },
        createdAt: now,
        updatedAt: now,
        deletedAt: null as unknown as Date,
        messages: [],
        contact: {
          id: draftContactID,
          name: contact.name,
          phone: contact.phone,
          avatar: null,
          createdAt: now,
          updatedAt: now,
          deletedAt: null as unknown as Date,
          chat: null as any,
        },
      },
    };

    const nextChatUrl = `/chat/${ draftChatID }`;

    draftServiceRef.current = draftService;
    storeDraftService( draftService );
    if( isDraftService( selectedServiceChat ) && selectedServiceChat?.chat.id ) removeDraftService( selectedServiceChat.chat.id );
    selectedChatIdRef.current = undefined;
    messagesRequestRef.current++;
    setMessages([]);
    setSelectedServiceChat( null );
    window.setTimeout(() => navigate( nextChatUrl ), 0 );
  }, [ channel, navigate, selectedServiceChat, user ]);

  const handleCloseServiceChat = useCallback(() => {
    if( isDraftService( selectedServiceChat ) && selectedServiceChat?.chat.id ) removeDraftService( selectedServiceChat.chat.id );
    setShowServiceMenu( false );
    setShowAttachmentMenu( false );
    selectedChatIdRef.current = undefined;
    messagesRequestRef.current++;
    setSelectedServiceChat( null );
    navigate( "/chats" );
  }, [ navigate, selectedServiceChat ]);

  const markSelectedChatRead = async ( service: Service ) => {
    if( !shouldMarkServiceChatRead( service )) return;

    const response = await simpleApiCall<string, boolean>( markChatRead, service.chat.id, () => {}, {
      showSuccessMessage: false,
      showErrorMessage: false,
    });

    if( response === null ) return;

    setSelectedServiceChat(( selectedServiceChat ) => {
      if( !selectedServiceChat || selectedServiceChat.id !== service.id ) return selectedServiceChat;

      return { ...selectedServiceChat, unReadCount: 0 };
    });

    setServices(( services ) => services.map(( item ) => item.id === service.id ? ({ ...item, unReadCount: 0 }) : item ));
  };

  const cleanFilters = () => {
    setSearch('');
    setQueue( defaultQueue as Queue );
    setChannel( defaultChannel );
    setStatus( ServiceStatus.PROGRESS );

    setShowMenuFilter( false );

    setServicesPage( 1 );
  };

  const loadQueues = async () => {
    const response = await simpleApiCall<GetAllQueuesPayload, Array<Queue>>( getAllQueues, undefined );

    if( response !== null ) setQueues([ defaultQueue as Queue, ...response ]);
  };

  const loadChannels = async () => {
    const response = await simpleApiCall<undefined, Array<Channel>>( listChannels, undefined, () => {}, {
      showSuccessMessage: false,
      showErrorMessage: false,
    });

    if( response !== null ) setChannels([ defaultChannel, ...response ]);
  };

  const loadTotalWaiting = async ( search: string, queueId?: string, channelId?: string ) => {
    const requestId = ++totalWaitingRequestRef.current;
    const response = await simpleApiCall<GetTotalWaiting, number>( getTotalWaiting, { queueId, channelId, search }, () => {}, {
      showSuccessMessage: false,
      showErrorMessage: false,
    });

    if( response !== null && requestId === totalWaitingRequestRef.current ) setTotalWaiting( response );
  };

  const loadServices = async ( page: number ) => {
    if( page <= 0 ) return;

    const paginationKey = servicesPaginationKey( page );
    if( !beginPaginationRequest( servicesPaginationInFlightRef, servicesPaginationRetryAtRef, paginationKey, setLoadingServices )) return;

    const requestId = ++servicesRequestRef.current;

    try {
      if( status === ServiceStatus.CONVERSATIONS ) {
        const response = await paginatedApiCall<GetConversationsPayload, Chat>( getConversations, {
          page,
          perPage: perPageServices,
          queueId: queue?.id,
          channelId: channel?.id,
          search: debouncedSearch,
          order,
        }, () => {});

        if( response === null ) {
          markPaginationRequestFailed( servicesPaginationRetryAtRef, paginationKey );
          return;
        }
        if( response !== null && requestId === servicesRequestRef.current ) {
          const results = response.results.map(( chat ) => conversationToService( chat, user ));

          clearPaginationRequestFailure( servicesPaginationRetryAtRef, paginationKey );

          if( page > 1 && results.length === 0 ) {
            setServicesTotal( servicesRef.current.length );
            return;
          }

          if( page === 1 ) {
            servicesRef.current = results;
            setServices( results );
          }
          else setServices(( services ) => {
            const nextServices = [ ...services, ...results ];
            servicesRef.current = nextServices;
            return nextServices;
          });

          setServicesTotal( response.total );
          setServicesPage( page + 1 );
        }

        return;
      }

      const response = await paginatedApiCall<GetServicesPayload, Service>( getServices, {
        page,
        perPage: perPageServices,
        status,
        queueId: queue?.id,
        channelId: channel?.id,
        search: debouncedSearch,
        order,
      }, () => {});

      if( response === null ) {
        markPaginationRequestFailed( servicesPaginationRetryAtRef, paginationKey );
        return;
      }
      if( response !== null && requestId === servicesRequestRef.current ) {
        const results = response.results.map(( service ) => normalizeService( service, assignedRole ));

        clearPaginationRequestFailure( servicesPaginationRetryAtRef, paginationKey );

        if( page > 1 && results.length === 0 ) {
          setServicesTotal( servicesRef.current.length );
          return;
        }

        if( page === 1 ) {
          servicesRef.current = results;
          setServices( results );
        }
        else setServices(( services ) => {
          const nextServices = [ ...services, ...results ];
          servicesRef.current = nextServices;
          return nextServices;
        });

        setServicesTotal( response.total );

        setServicesPage( page + 1 );
      }
    } finally {
      finishPaginationRequest( servicesPaginationInFlightRef, paginationKey, setLoadingServices );
    }
  };

  const onScrollServicesCapture = () => {
    const scrollElement = scrollServicesRef.current;

    if ( !scrollElement || loading ) return;

    if ( scrollElement.scrollTop + scrollElement.clientHeight >= scrollElement.scrollHeight - 10 ) {
      if( services.length < servicesTotal ) {
        loadServices( servicesPage );
      }
    }
  };

  const onScrollMessagesCapture = () => {
    const scrollElement = scrollMessagesRef.current;

    if ( !scrollElement || loading ) return;

    if( forceScrollToLatestMessageRef.current || Date.now() < suppressScrollToBottomButtonUntilRef.current ) {
      setShowScrollToBottom( false );
      return;
    }

    if ( Math.abs( scrollElement.scrollTop ) + scrollElement.clientHeight >= scrollElement.scrollHeight - 10 ) {
      if( messages.length < messagesTotal ) {
        loadMessages( selectedServiceChat?.chat.id as string, messagesPage );
      }
    }

    setShowScrollToBottom( Math.abs( scrollElement.scrollTop ) > scrollToBottomTolerance );
  };

  const handleScrollToBottom = () => {
    const scrollElement = scrollMessagesRef.current;

    if ( !scrollElement || loading ) return;

    suppressScrollToBottomButtonUntilRef.current = Date.now() + suppressScrollToBottomButtonMs;
    setShowScrollToBottom( false );

    scrollElement.scrollTo({
      top: 0,
    });
  };

  const loadMessages = async ( chatId: string, page: number ) => {
    if( !chatId || page <= 0 || selectedChatIdRef.current !== chatId ) return;

    const paginationKey = messagesPaginationKey( chatId, page );
    if( !beginPaginationRequest( messagesPaginationInFlightRef, messagesPaginationRetryAtRef, paginationKey, setLoadingMessages )) return;

    try {
      const requestId = ++messagesRequestRef.current;
      setLoadingMessagesPage( page );
      const response = await paginatedApiCall<GetMessagesByChatPayload, Message>(
        getMessagesByChat,
        { page, perPage: perPageMessages, chatId },
        () => {},
        {
          showErrorMessage: false,
        },
      );

      if( response === null ) {
        markPaginationRequestFailed( messagesPaginationRetryAtRef, paginationKey );
        return;
      }

      if( response !== null && requestId === messagesRequestRef.current && selectedChatIdRef.current === chatId ) {
        const results = response.results.map(( message ) => normalizeMessage( message, assignedRole ));

        clearPaginationRequestFailure( messagesPaginationRetryAtRef, paginationKey );

        if( page > 1 && results.length === 0 ) {
          setMessagesTotal(( total ) => Math.min( total, messages.length ));
          return;
        }

        if ( page === 1 ) setMessages( results );
        else setMessages(( messages ) => [ ...messages, ...results ]);

        setMessagesTotal( response.total );

        setMessagesPage( page + 1 );
      }
    } finally {
      finishPaginationRequest( messagesPaginationInFlightRef, paginationKey, setLoadingMessages );
    }
  };

  const loadMessageContext = async ( messageId: string ): Promise<boolean> => {
    const chatID = selectedServiceChat?.chat.id;
    if( !chatID || !messageId ) return false;

    const response = await simpleApiCall<GetMessageContextPayload, MessageContext>(
      getMessageContext,
      { chatId: chatID, messageId, perPage: perPageMessages + 1 },
      () => {},
      {
        showSuccessMessage: false,
        showErrorMessage: false,
      },
    );

    if( !response ) return false;

    const contextMessages = [ ...response.before, response.target, ...response.after ].map(( message ) => normalizeMessage( message, assignedRole ));
    setMessages(( messages ) => {
      const mergedMessages = mergeMessageWindow( messages, contextMessages );
      setMessagesTotal(( total ) => Math.max( total, mergedMessages.length ));
      return mergedMessages;
    });

    return true;
  };

  const groupMessages = ( messages: Array<Message> ) => {
    const dates: Array<string> = [];

    messages
      .forEach(({ createdAt }) => {
        const date = format( createdAt, "dd/MM/yyyy");

        if( !dates.includes( date )) dates.push( date );
      });

    return dates.map(( date ) => ({
      date,
      messages: messages
        .filter(({ createdAt }) => format( createdAt, "dd/MM/yyyy") === date )
        .map(( message, index, messages ) => {
          message.show = false;

          const nextMessage = messages[ index + 1 ];

          if( nextMessage ) {
            if( message.ownerType !== nextMessage.ownerType ) message.show = true;
            else if([ message?.contact?.id, message?.user?.id ].includes( nextMessage.id )) message.show = true;
          } else message.show = true;

          return message;
        }),
    }));
  };

  const handleTransferService = async ( queueId?: string, comment?: string ) => {
    if( serviceMutationInFlightRef.current ) return false;

    serviceMutationInFlightRef.current = true;

    try {
      const response = await simpleApiCall<TransferServicePayload, boolean>( transferService, { id: selectedServiceChat?.id as string, queueId, comment });

      if( response !== null ) {
        setShowTransferServiceModal( false );
        setShowTransferModalConfirm( false );
        handleCloseServiceChat();
        setServicesPage( 1 );
        void loadServices( 1 );
        void loadTotalWaiting( debouncedSearch, queue?.id, channel?.id );

        return true;
      }

      return false;
    } finally {
      serviceMutationInFlightRef.current = false;
    }
  };

  const handleStartSelectedConversation = async ( queueId?: string ) => {
    if( serviceMutationInFlightRef.current || !selectedServiceChat || !isHistoricalConversationService( selectedServiceChat )) return false;

    const contact = selectedServiceChat.chat.contact;
    serviceMutationInFlightRef.current = true;

    try {
      const response = await simpleApiCall<StartConversationPayload, Service>( startConversation, {
        channelId: selectedServiceChat.chat.channelId || selectedServiceChat.chat.channel?.id || selectedServiceChat.chat.channel?.id,
        name: contact.name,
        phone: contact.phone,
        queueId,
      });

      if( response === null ) return false;

      const persistedService = normalizeService( response, assignedRole );
      removeDraftService( selectedServiceChat.chat.id );
      draftServiceRef.current = null;
      setShowStartConversationModal( false );
      setStatus( persistedService.status );
      setServicesPage( 1 );
      setSelectedServiceChat( persistedService );
      setServices(( services ) => mergeRealtimeServiceList( services, persistedService, persistedService.status, queue?.id, debouncedSearch, assignedRole, channel?.id ));
      void loadServices( 1 );
      void loadTotalWaiting( debouncedSearch, queue?.id, channel?.id );

      return true;
    } finally {
      serviceMutationInFlightRef.current = false;
    }
  };

  const handleAbandonService = async () => {
    if( serviceMutationInFlightRef.current ) return;

    serviceMutationInFlightRef.current = true;

    try {
      const response = await simpleApiCall<DefaultServicePayload, boolean>( abandonService, { id: selectedServiceChat?.id as string });

      if( response !== null ) {
        handleCloseServiceChat();
      }
    } finally {
      serviceMutationInFlightRef.current = false;
    }
  };

  const handleCloseService = async ( comment?: string ) => {
    if( serviceMutationInFlightRef.current ) return;

    serviceMutationInFlightRef.current = true;

    try {
      const response = await simpleApiCall<DefaultServicePayload, boolean>( closeService, { id: selectedServiceChat?.id as string, comment });

      if( response !== null ) {
        handleCloseServiceChat();
      }
    } finally {
      serviceMutationInFlightRef.current = false;
    }
  };

  const attServices = ( service: Service ) => {
    const normalizedService = normalizeService( service, assignedRole );

    setServices(( services ) => mergeRealtimeServiceList( services, normalizedService, status, queue?.id, debouncedSearch, assignedRole, channel?.id ));

    if( normalizedService.id === selectedServiceChat?.id ) {
      setSelectedServiceChat(( selectedServiceChat ) => selectedServiceChat ? mergeSelectedRealtimeService( selectedServiceChat, normalizedService ) : normalizedService );
    }

    loadTotalWaiting( debouncedSearch, queue?.id, channel?.id );
  };

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch( search );
    }, searchDebounceMs );

    return () => {
      window.clearTimeout( timeoutId );
    };
  }, [ search ]);

  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [ attachments ]);

  useEffect(() => {
    attachmentTypeRef.current = attachmentType;
  }, [ attachmentType ]);

  useEffect(() => {
    internalMessageRef.current = internalMessage;
  }, [ internalMessage ]);

  useLayoutEffect(() => {
    selectedServiceChatRef.current = selectedServiceChat;
  }, [ selectedServiceChat ]);

  useLayoutEffect(() => {
    servicesRef.current = services;
  }, [ services ]);

  useEffect(() => {
    selectedChatIdRef.current = selectedServiceChat?.chat.id;
  }, [ selectedServiceChat?.chat.id ]);

  useLayoutEffect(() => {
    if( !forceScrollToLatestMessageRef.current ) return;

    scrollMessagesToBottomNow( forceScrollToMessageIDRef.current );
    const timeoutId = window.setTimeout(() => {
      scrollMessagesToBottomNow( forceScrollToMessageIDRef.current );
      forceScrollToLatestMessageRef.current = false;
      forceScrollToMessageIDRef.current = null;
    }, 0 );

    return () => window.clearTimeout( timeoutId );
  }, [ messages, scrollMessagesToBottomNow ]);

  useEffect(() => {
    if( !chatId ) {
      draftServiceRef.current = null;
      selectedChatIdRef.current = undefined;
      messagesRequestRef.current++;
      setSelectedServiceChat( null );
      return;
    }

    if( selectedServiceChat?.chat.id === chatId ) return;

    const routeDraftService = draftServiceRef.current?.chat.id === chatId ? draftServiceRef.current : loadDraftService( chatId );
    if( chatId.startsWith( draftIDPrefix ) || routeDraftService ) {
      const draftService = routeDraftService;
      if( !draftService ) {
        navigate( "/chats", { replace: true });
        return;
      }

      draftServiceRef.current = draftService;
      messagesRequestRef.current++;
      setMessages([]);
      setSelectedServiceChat( draftService );
      return;
    }

    draftServiceRef.current = null;

    const loadServiceFromRoute = async () => {
      const response = await simpleApiCall<string, Service>( getServiceByChatId, chatId, () => {}, {
        showErrorMessage: true,
      });

      if( response === null ) {
        setSelectedServiceChat( null );
        return;
      }

      const currentSelectedService = selectedServiceChatRef.current?.chat.id === chatId ? selectedServiceChatRef.current : undefined;
      const cachedService = currentSelectedService ?? servicesRef.current.find(( service ) => service.chat.id === chatId );
      const normalizedService = mergeServiceChannelFallback(
        normalizeService( response, assignedRole ),
        cachedService,
      );
      const nextStatus = activeServiceTabStatus( normalizedService );
      if( nextStatus ) setStatus(( currentStatus ) => currentStatus === nextStatus ? currentStatus : nextStatus );
      setSelectedServiceChat( normalizedService );
    };

    void loadServiceFromRoute();
    // eslint-disable-next-line
  }, [ chatId ]);

  useEffect(() => {
    setMessages([]);

    if( selectedServiceChat?.chat.id && isDraftService( selectedServiceChat ) && selectedServiceChat.chat.id.startsWith( draftIDPrefix )) {
      setMessages([]);
    }
    else if( selectedServiceChat?.chat.id ) {
      loadMessages( selectedServiceChat.chat.id, 1 );
      markSelectedChatRead( selectedServiceChat );
    }
    else {
      setAttachmentType( null );
      setAttachments( [] );
      resetUpload();
    }
    // eslint-disable-next-line
  }, [ selectedServiceChat?.chat.id ]);

  useEffect(() => {
    const handleEscape = ( event: KeyboardEvent ) => {
      const dialogOpen = showTransferModalConfirm
        || showAbandonModalConfirm
        || showCloseModalConfirm
        || showTransferServiceModal
        || showStartConversationModal
        || Boolean( document.querySelector('[role="dialog"][aria-modal="true"], [data-escape-layer="true"]') );
      if( !shouldCloseServiceChatOnEscape( event, selectedServiceChat, dialogOpen )) return;

      event.preventDefault();
      handleCloseServiceChat();
    };

    window.addEventListener( "keydown", handleEscape, true );

    return () => window.removeEventListener( "keydown", handleEscape, true );
  }, [ handleCloseServiceChat, selectedServiceChat, showTransferModalConfirm, showAbandonModalConfirm, showCloseModalConfirm, showTransferServiceModal, showStartConversationModal ]);

  useEffect(() => {
    if ( !socket ) return;

    const handleNewMessage = ({ service }: { service: Service }) => {
      const normalizedService = normalizeService( service, assignedRole );
      setServices(( services ) => mergeRealtimeServiceList( services, normalizedService, status, queue?.id, debouncedSearch, assignedRole, channel?.id ));

      if( selectedServiceChat?.id === normalizedService.id ) {
        setSelectedServiceChat(( selectedServiceChat ) => selectedServiceChat ? mergeSelectedRealtimeService( selectedServiceChat, normalizedService ) : normalizedService );

        setMessages(( messages ) => mergeRealtimeMessages( messages, normalizedService.chat.messages ?? [] ));
      }

      loadTotalWaiting( debouncedSearch, queue?.id, channel?.id );
    };

    socket.on("channel:new-message", handleNewMessage );

    return () => {
      socket.off("channel:new-message", handleNewMessage );
    };
    // eslint-disable-next-line
  }, [ socket, selectedServiceChat, status, queue?.id, channel?.id, debouncedSearch, assignedRole ]);

  useEffect(() => {
    if ( !socket ) return;

    socket.on("service:att:transfer", ({ service }: { service: Service }) => {
      attServices( service );
    });

    return () => {
      socket.off("service:att:transfer");
    };
    // eslint-disable-next-line
  }, [ socket, debouncedSearch, queue, channel, selectedServiceChat ]);

  useEffect(() => {
    if ( !socket ) return;

    socket.on("service:att:abandon", ({ service }: { service: Service }) => {
      attServices( service );
    });

    return () => {
      socket.off("service:att:abandon");
    };
    // eslint-disable-next-line
  }, [ socket, search, queue, channel, selectedServiceChat ]);

  useEffect(() => {
    if ( !socket ) return;

    socket.on("service:att:close", ({ service }: { service: Service }) => {
      attServices( service );
    });

    return () => {
      socket.off("service:att:close");
    };
    // eslint-disable-next-line
  }, [ socket, search, queue, channel, selectedServiceChat ]);

  useEffect(() => {
    if ( !socket ) return;

    socket.on("service:att:start", ({ service }: { service: Service }) => {
      attServices( service );
    });

    return () => {
      socket.off("service:att:start");
    };
    // eslint-disable-next-line
  }, [ socket, search, queue, channel, selectedServiceChat ]);

  useEffect(() => {
    if ( !socket ) return;

    socket.on("service:new", () => {
      if( status === ServiceStatus.WAITING ) {
        servicesRequestRef.current++;
        void loadServices( 1 );
      }
      loadTotalWaiting( debouncedSearch, queue?.id, channel?.id );
    });

    return () => {
      socket.off("service:new");
    };
    // eslint-disable-next-line
  }, [ socket, debouncedSearch, queue, channel, selectedServiceChat ]);

  useEffect(() => {
    if ( !socket ) return;

    socket.on("message:att-status", ({ messagesKeys, status }: { messagesKeys: Array<string>, status: MessageStatus }) => {
      setServices(( services ) => {
        let _services = [ ...services ];

        _services = _services.map(( service ) => {
          return {
            ...service,
            chat: {
              ...service.chat,
              messages: updateMessagesStatus( service.chat.messages, messagesKeys, status ),
            },
          };
        });

        return sortRealtimeServices( _services );
      });

      if( selectedServiceChat?.id ) {
        setMessages(( messages ) => updateMessagesStatus( messages, messagesKeys, status ));
      }
    });

    return () => {
      socket.off("message:att-status");
    };
    // eslint-disable-next-line
  }, [ socket, selectedServiceChat ]);

  useEffect(() => {
    if ( !socket ) return;

    const applyPresence = ( presence: PresenceItem ) => {
      setServices(( services ) => applyPresenceToServices( services, presence ));
      setSelectedServiceChat(( service ) => service ? applyPresenceToService( service, presence ) : service );
    };
    const handleSnapshot = ( snapshot: PresenceSnapshot ) => {
      snapshot.users?.forEach( applyPresence );
      snapshot.contacts?.forEach( applyPresence );
    };

    socket.on("presence:snapshot", handleSnapshot);
    socket.on("presence:update", applyPresence);

    return () => {
      socket.off("presence:snapshot", handleSnapshot);
      socket.off("presence:update", applyPresence);
    };
    // eslint-disable-next-line
  }, [ socket ]);

  useEffect(() => {
    setServicesPage( 1 );
    servicesRequestRef.current++;
    loadServices( 1 );
    // eslint-disable-next-line
  }, [ debouncedSearch, queue, channel, status, order ]);

  useEffect(() => {
    loadTotalWaiting( debouncedSearch, queue?.id, channel?.id );
    // eslint-disable-next-line
  }, [ debouncedSearch, queue, channel ]);

  useEffect(() => {
    loadQueues();
    loadChannels();

    const audioRate = getAudioRateLocalStorage() ?? 1;

    setAudioRate( audioRate );
    // eslint-disable-next-line
  }, []);

  return useMemo(() => ({
    showMenuFilter,
    showScrollToBottom,
    search,
    queue,
    channel,
    status,
    queues,
    channels,
    services,
    groupedMessages: groupMessages( messages ),
    scrollServicesRef,
    scrollMessagesRef,
    loadingServices,
    loadingMessages,
    loadingMessagesPage,
    selectedServiceChat,
    editor,
    message,
    internalMessage,
    replyTargetMessage,
    editTargetMessage,
    attachments,
    multipartUploadState,
    channelIsConnected: selectedServiceChannelIsConnected,
    order,
    totalWaiting,
    showServiceMenu,
    showAttachmentMenu,
    showTransferModalConfirm,
    showAbandonModalConfirm,
    showCloseModalConfirm,
    showTransferServiceModal,
    showStartConversationModal,
    currentAudio,
    audioRate,
    attachmentType,
    attachmentInputRef,
    handleChangeAttachment,
    handleRemoveAttachment,
    handlePlayAudio,
    handleStopAudio,
    handleNextAudioRate,
    handleToggleShowServiceMenu,
    handleToggleShowAttachmentMenu,
    handleToggleShowTransferModalConfirm,
    handleToggleShowAbandonModalConfirm,
    handleToggleShowCloseModalConfirm,
    handleToggleShowTransferServiceModal,
    handleToggleShowStartConversationModal,
    handleToggleInternalMessage,
    handleSelectServiceChat,
    handleStartConversationFromSharedContact,
    handleCloseServiceChat,
    cleanFilters,
    setShowMenuFilter,
    setSearch,
    setQueue,
    setChannel,
    setStatus,
    onScrollServicesCapture,
    onScrollMessagesCapture,
    handleScrollToBottom,
    loadMessageContext,
    handleSendMessage,
    handleRetryUploadMessage,
    handleRetryMessage,
    retryingMessageIds,
    handleSendSavedSticker,
    handleReplyToMessage,
    handleClearReplyToMessage,
    clearEditTargetMessage,
    handleEditMessage,
    handleDeleteMessage,
    handleReactToMessage,
    handleToggleOrder,
    handleTransferService,
    handleStartSelectedConversation,
    handleAbandonService,
    handleCloseService,
    changeAttachmentType,
    // eslint-disable-next-line
  }), [
    showMenuFilter,
    showScrollToBottom,
    search,
    queue,
    channel,
    status,
    queues,
    channels,
    services,
    messages,
    scrollServicesRef,
    scrollMessagesRef,
    loadingServices,
    loadingMessages,
    loadingMessagesPage,
    selectedServiceChat,
    loadMessageContext,
    editor,
    message,
    internalMessage,
    replyTargetMessage,
    editTargetMessage,
    attachments,
    multipartUploadState,
    selectedServiceChannelIsConnected,
    order,
    totalWaiting,
    showServiceMenu,
    showAttachmentMenu,
    showTransferModalConfirm,
    showAbandonModalConfirm,
    showCloseModalConfirm,
    showTransferServiceModal,
    showStartConversationModal,
    currentAudio,
    audioRate,
    attachmentType,
    attachmentInputRef,
    handleStartConversationFromSharedContact,
    handleSendSavedSticker,
    handleRetryUploadMessage,
    handleRetryMessage,
    retryingMessageIds,
    handleReplyToMessage,
    handleClearReplyToMessage,
    clearEditTargetMessage,
    handleEditMessage,
    handleDeleteMessage,
    handleReactToMessage,
  ]);
};
